import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { T, useIsDesktop, Bone } from '../reportTokens';
import DrillFrame, { MiniKPI } from './DrillFrame';
import SectionError from '../SectionError';
import SectionEmpty from '../SectionEmpty';
import { ApiTypeBadge, ApiStatusPill, fmtNum, fmtMoney, truncate, dateTickInterval, makeDateTickFormatter, DATE_AXIS_PADDING, dateAxisRightMargin, dateAxisTickProps, dateAxisExtraHeight } from './offersData';
import { localizeCurrencyText, useCurrencyRevision } from '../../../utils/currency';
import type { OfferDetailResponse } from '../../../interface/offersReportInterface';

const SECTION_TITLE_STYLE: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 };
const META_LABEL_STYLE: React.CSSProperties = { fontSize: 10, color: T.t3, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 };

// Top Items row name — a long backend item name ("Hyderabadi Chicken Dum
// Family Pack Biryani") was overflowing the row's flex layout on mobile instead
// of staying within the rank-badge/bar/count row, so the name is now
// character-truncated (never left to CSS ellipsis alone) with a reveal for the
// full text: hover on desktop, tap on mobile — same affordance as the Top
// Offers table's OfferNameDesktopCell / OfferNameMobileCell in OffersReportScreen.
const ITEM_NAME_TRUNC_DESKTOP = 28;
const ITEM_NAME_TRUNC_MOBILE = 22;

function ItemNameDesktopCell({ name: rawName }: { name: string }) {
    const [hover, setHover] = useState(false);
    const name = localizeCurrencyText(rawName);
    const isTruncated = name.length > ITEM_NAME_TRUNC_DESKTOP;
    return (
        <span
            style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <span style={{ fontSize: 12, color: T.t1, fontWeight: 600 }}>{truncate(name, ITEM_NAME_TRUNC_DESKTOP)}</span>
            {isTruncated && hover && (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        left: 0,
                        bottom: 'calc(100% + 6px)',
                        zIndex: 600,
                        background: T.t1,
                        color: T.card,
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        padding: '6px 10px',
                        borderRadius: 6,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        whiteSpace: 'normal',
                        width: 'max-content',
                        maxWidth: 260,
                        pointerEvents: 'none',
                    }}
                >
                    {name}
                </span>
            )}
        </span>
    );
}

function ItemNameMobileCell({ name: rawName }: { name: string }) {
    const [expanded, setExpanded] = useState(false);
    const name = localizeCurrencyText(rawName);
    const isTruncated = name.length > ITEM_NAME_TRUNC_MOBILE;
    return (
        <div
            onClick={isTruncated ? (e) => { e.stopPropagation(); setExpanded((v) => !v); } : undefined}
            style={{
                fontSize: 12,
                color: T.t1,
                fontWeight: 600,
                cursor: isTruncated ? 'pointer' : 'inherit',
                whiteSpace: expanded ? 'normal' : 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
            }}
        >
            {expanded ? name : truncate(name, ITEM_NAME_TRUNC_MOBILE)}
        </div>
    );
}

// Individual Offer drill — fed by GET /sales/offers/detail (the Top Offer stamp
// KPI's drilldown). Like OfferRedemptionTrendDrill, each panel owns its own
// failure/empty rendered in-place (shaped like that section) rather than
// collapsing the whole drill into one message. Stamp KPIs stay put on
// failure/empty and show "—".
export default function OfferIndividualDrill({ data, loading, failure, onRetry }: {
    data: OfferDetailResponse | null;
    loading?: boolean;
    failure?: boolean;
    onRetry?: () => void;
}) {
    const isDesktop = useIsDesktop();
    // Re-render when the outlet's country resolves, so the currency symbol inside
    // backend text (type label, item names) is rewritten for non-US outlets.
    useCurrencyRevision();
    const isLoading = !!loading && !data;

    const daily = data?.dailyTrend ?? [];
    const topItems = data?.topItems ?? [];
    // Order Type Breakdown — ranked by count, matching the redemption-trend drill.
    const orderTypes = [...(data?.orderTypeBreakdown ?? [])].sort((a, b) => b.count - a.count);
    const codes = data?.promoCodes ?? [];
    const maxItem = topItems.length ? Math.max(...topItems.map((i) => i.orderCount)) : 0;
    // Order-level offers have no item breakdown by design — the API always returns
    // `topItems: []` when applicationLevel is "Order". Drop the section instead of
    // showing a misleading "no top items" empty state, and let Order Type take the
    // full row.
    const isOrderLevel = (data?.applicationLevel ?? '').trim().toLowerCase() === 'order';
    // The shimmer previews only what is guaranteed to render. applicationLevel
    // arrives *with* the response (it is on no other endpoint, and the slice nulls
    // offerDetail on request), so while loading we cannot know whether Top Items
    // applies — and a labelled skeleton an order-level offer then drops would
    // preview a layout that never appears. Order Type is unconditional, so the
    // loading row shows that panel alone at full width and Top Items appears only
    // once the response confirms the offer is item-level. On failure data is null
    // and isLoading is false, so the section returns and keeps its retryable error.
    const showTopItems = !isLoading && !isOrderLevel;
    const maxOT = orderTypes.length ? Math.max(...orderTypes.map((o) => o.count)) : 0;
    const orderTypeTotal = orderTypes.reduce((s, o) => s + o.count, 0);
    // Chart + placeholder height. Below desktop the x-axis labels are rotated
    // (`dateAxisTickProps`) and the extra height pays for the taller tick band.
    const chartHeight = (isDesktop ? 180 : 140) + dateAxisExtraHeight(isDesktop);

    return (
        <DrillFrame isDesktop={isDesktop}>
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 18, boxShadow: T.shadow }}>
                {/* ── Offer metadata ─────────────────────────────────────────── */}
                <div style={{ background: T.bg, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)', gap: 14, rowGap: 14 }}>
                        <div><div style={META_LABEL_STYLE}>Type</div><div style={{ marginTop: 6 }}>{isLoading ? <Bone w={60} h={16} r={4} /> : data ? <ApiTypeBadge type={data.offerType} /> : '—'}</div></div>
                        <div><div style={META_LABEL_STYLE}>Apply</div><div style={{ fontSize: 13, color: T.t1, marginTop: 6 }}>{isLoading ? <Bone w={80} h={14} r={4} /> : data ? `${data.applicationLevel} · ${data.applyMode}` : '—'}</div></div>
                        <div><div style={META_LABEL_STYLE}>Stacking</div><div style={{ fontSize: 13, color: T.t1, marginTop: 6 }}>{isLoading ? <Bone w={70} h={14} r={4} /> : data ? data.stackingMode : '—'}</div></div>
                        <div><div style={META_LABEL_STYLE}>Status</div><div style={{ marginTop: 6 }}>{isLoading ? <Bone w={60} h={16} r={4} /> : data ? <ApiStatusPill status={data.status} /> : '—'}</div></div>
                        <div style={isDesktop ? { gridColumn: 'span 4' } : { gridColumn: 'span 2' }}>
                            <div style={META_LABEL_STYLE}>Range</div>
                            <div style={{ fontSize: 13, color: T.t1, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{isLoading ? <Bone w={160} h={14} r={4} /> : data ? `${data.startDate} → ${data.endDate || 'No end date'}` : '—'}</div>
                        </div>
                    </div>
                </div>

                {/* ── Stamp KPIs — on failure/empty the cards stay put and show "—" ──
                    Some offers report `metricsAvailable: false`, in which case the
                    numeric fields (redemptions / totalDiscount / avgOrderValue) can
                    arrive null. fmtNum/fmtMoney are null-safe (→ "—"), so these render
                    a dash instead of throwing and blanking the whole report. */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <MiniKPI label="Redemptions" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtNum(data.redemptions) : '—'} />
                    <MiniKPI label="Total Discount" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtMoney(data.totalDiscount) : '—'} />
                    <MiniKPI label="Avg Order Value" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtMoney(data.avgOrderValue) : '—'} />
                </div>

                {/* ── Redemptions by Day ─────────────────────────────────────── */}
                <div style={{ marginBottom: 16 }}>
                    <div style={SECTION_TITLE_STYLE}>Redemptions by Day</div>
                    {isLoading ? (
                        <Bone h={chartHeight} r={8} />
                    ) : failure ? (
                        <SectionError kpiName="offer redemptions" onRetry={onRetry} size="medium" minHeight={chartHeight} />
                    ) : daily.length === 0 ? (
                        <SectionEmpty kpiName="offer redemptions" size="medium" minHeight={chartHeight} />
                    ) : (
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={daily} margin={{ top: 5, right: dateAxisRightMargin(isDesktop), left: -15, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} padding={DATE_AXIS_PADDING} tickFormatter={makeDateTickFormatter(daily.map((d) => d.date))} interval={dateTickInterval(daily.length)} {...dateAxisTickProps(isDesktop)} />
                                <YAxis tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11 }} />
                                <Line type="monotone" dataKey="count" stroke={T.accent} strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* ── Top Items + Order Type ─────────────────────────────────── */}
                <div style={{ display: 'grid', gridTemplateColumns: isDesktop && showTopItems ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 14 }}>
                    {showTopItems && (
                    <div>
                        <div style={SECTION_TITLE_STYLE}>Top Items</div>
                        {/* No skeleton branch — `showTopItems` is false while loading. */}
                        {failure ? (
                            <SectionError kpiName="top items" onRetry={onRetry} size="small" minHeight={120} />
                        ) : topItems.length === 0 ? (
                            <SectionEmpty kpiName="top items" size="small" minHeight={120} />
                        ) : (
                            topItems.map((item, i) => (
                                <div key={item.itemName} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                    <div style={{ width: 18, height: 18, borderRadius: 4, background: i < 3 ? T.accentSoft : T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: i < 3 ? T.accent : T.t3 }}>{i + 1}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        {isDesktop ? <ItemNameDesktopCell name={item.itemName} /> : <ItemNameMobileCell name={item.itemName} />}
                                        <div style={{ background: T.hover, borderRadius: 2, height: 4, marginTop: 4 }}>
                                            <div style={{ width: `${maxItem > 0 ? (item.orderCount / maxItem) * 100 : 0}%`, height: '100%', background: T.accent, borderRadius: 2 }} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 11, color: T.t2, fontWeight: 500 }}>{fmtNum(item.orderCount)}</div>
                                </div>
                            ))
                        )}
                    </div>
                    )}
                    <div>
                        <div style={SECTION_TITLE_STYLE}>Order Type</div>
                        {isLoading ? (
                            [0, 1, 2, 3].map((i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                    <Bone w={70} h={11} r={4} />
                                    <Bone w={null} h={6} r={3} style={{ flex: 1 }} />
                                    <Bone w={40} h={11} r={4} />
                                </div>
                            ))
                        ) : failure ? (
                            <SectionError kpiName="order type breakdown" onRetry={onRetry} size="small" minHeight={120} />
                        ) : orderTypes.length === 0 ? (
                            <SectionEmpty kpiName="order type breakdown" size="small" minHeight={120} />
                        ) : (
                            orderTypes.map((r) => (
                                <div key={r.orderType} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                    <div style={{ width: 70, fontSize: 11, color: T.t2 }}>{r.orderType}</div>
                                    <div style={{ flex: 1, background: T.hover, borderRadius: 2, height: 6 }}>
                                        <div style={{ width: `${maxOT > 0 ? (r.count / maxOT) * 100 : 0}%`, height: '100%', background: T.accent, borderRadius: 2 }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: T.t1, fontWeight: 600, width: 60, textAlign: 'right' }}>{fmtNum(r.count)}{orderTypeTotal > 0 ? ` (${Math.round((r.count / orderTypeTotal) * 100)}%)` : ''}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* ── Promo Code Performance (only when the offer has codes) ──── */}
                {!isLoading && !failure && codes.length > 0 && (
                    <div>
                        <div style={SECTION_TITLE_STYLE}>Promo Code Performance</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font }}>
                                <thead>
                                    <tr>
                                        {['Code', 'Uses', 'Remaining', 'Revenue', 'Last Used'].map((h) => (
                                            <th key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.divider}` }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {codes.map((c) => (
                                        <tr key={c.code} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                            <td style={{ fontSize: 12, color: T.t1, fontWeight: 600, padding: '8px 10px', fontFamily: 'monospace' }}>{c.code}</td>
                                            <td style={{ fontSize: 12, color: T.t1, padding: '8px 10px' }}>{c.uses}</td>
                                            <td style={{ fontSize: 12, color: T.t2, padding: '8px 10px' }}>{c.remaining == null ? 'Unlimited' : c.remaining}</td>
                                            <td style={{ fontSize: 12, color: T.t1, padding: '8px 10px' }}>{fmtMoney(c.revenue)}</td>
                                            <td style={{ fontSize: 12, color: T.t2, padding: '8px 10px' }}>{c.lastUsed ? new Date(c.lastUsed).toLocaleDateString() : 'Never'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </DrillFrame>
    );
}
